import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { partnerService } from '../../services/partner.service';
import { Partner } from '../../types';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Loading } from '../../components/ui/Loading';

export const PartnersPage = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'HOSPITAL',
    location: '',
    contactDetails: '',
    active: true
  });

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      setLoading(true);
      const data = await partnerService.getAllPartners();
      setPartners(data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        type: formData.type as Partner['type']
      };
      if (editingPartner) {
        await partnerService.updatePartner(editingPartner.id, data);
        toast.success('Partner updated successfully');
      } else {
        await partnerService.createPartner(data);
        toast.success('Partner created successfully');
      }
      setShowModal(false);
      resetForm();
      loadPartners();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save partner');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'HOSPITAL',
      location: '',
      contactDetails: '',
      active: true
    });
    setEditingPartner(null);
  };

  const handleEdit = (partner: Partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      type: partner.type,
      location: partner.location || '',
      contactDetails: partner.contactDetails || '',
      active: partner.active
    });
    setShowModal(true);
  };

  if (loading) return <Loading />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Partner Management</h1>
          <p className="text-gray-600">Manage healthcare partners and services</p>
        </div>
        <Button onClick={() => setShowModal(true)}>Add Partner</Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {partners.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    No partners found. Click "Add Partner" to create one.
                  </td>
                </tr>
              ) : (
                partners.map((partner) => (
                  <tr key={partner.id}>
                    <td className="px-6 py-4 whitespace-nowrap">{partner.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {partner.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{partner.location || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${partner.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {partner.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Button variant="secondary" size="sm" onClick={() => handleEdit(partner)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={editingPartner ? 'Edit Partner' : 'Add Partner'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Partner Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Select
            label="Type"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            options={[
              { value: 'HOSPITAL', label: 'Hospital' },
              { value: 'CLINIC', label: 'Clinic' },
              { value: 'DIAGNOSTIC_CENTER', label: 'Diagnostic Center' },
              { value: 'PHARMACY', label: 'Pharmacy' }
            ]}
          />
          <Input
            label="Location"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            required
          />
          <Input
            label="Contact Details"
            value={formData.contactDetails}
            onChange={(e) => setFormData({ ...formData, contactDetails: e.target.value })}
            placeholder="Phone, email, etc."
            required
          />
          <div className="flex items-center">
            <input
              type="checkbox"
              id="active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="mr-2"
            />
            <label htmlFor="active">Active</label>
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button type="submit">
              {editingPartner ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
